import { CandidateStatusDto, CompanySizeDto } from '@/api/generated';
import {
  quickTipsTranslation,
  QuickTipsTranslation,
} from '@/assets/locales/views/candidates/details/quickTips/quickTips.translation.en.ts';
import { CandidateTabsModel } from '@/views/candidates/details/model/CandidateTabs.model.ts';

export type CandidateStatusesTranslation = Record<CandidateStatusDto, string> & { sent: string };
export const candidateStatusesTranslation: CandidateStatusesTranslation = {
  [CandidateStatusDto.AwaitingDownload]: 'Awaiting CV preparation',
  [CandidateStatusDto.AwaitingExternalPick]: 'Awaiting eRecruiter',
  [CandidateStatusDto.AwaitingProcessing]: 'Awaiting processing',
  [CandidateStatusDto.CheckExternalProcess]: 'Check eRecruiter',
  [CandidateStatusDto.Downloading]: 'Preparing CV',
  [CandidateStatusDto.Error]: 'Error',
  [CandidateStatusDto.Imported]: 'Imported',
  [CandidateStatusDto.InProgress]: 'In progress',
  [CandidateStatusDto.InsufficientPositionDescription]: 'Insufficient position description',
  [CandidateStatusDto.New]: 'New',
  [CandidateStatusDto.NoFileFound]: 'No file found',
  [CandidateStatusDto.ProcessingOutdated]: 'Processing outdated',
  [CandidateStatusDto.ProcessNotReady]: 'Profile ready, analysis not started',
  [CandidateStatusDto.Seen]: 'Seen',
  [CandidateStatusDto.UnrelatedExperience]: 'Unrelated experience',
  sent: 'Sent to eRecruiter',
};
export type CompanySizeTranslation = Record<CompanySizeDto, string>;
export const companySizeTranslation: CompanySizeTranslation = {
  [CompanySizeDto.Big]: 'Big (250-999)',
  [CompanySizeDto.Enterprise]: 'Enterprise (1000+)',
  [CompanySizeDto.Medium]: 'Medium (100-249)',
  [CompanySizeDto.Micro]: 'Micro (1-9)',
  [CompanySizeDto.Small]: 'Small (10-99)',
};

export type AgreementsTranslation = {
  applicationDate: string;
  consentExpiresWithinMonth: string;
  consentForFutureRecruitmentsGiven: string;
  consentForFutureRecruitmentsGranted: string;
  cvFromCvSearch: string;
  dataProcessingConsentValidOverMonth: string;
  hasProcessingTimeWarning: string;
  limitedDataProcessing: string;
  noConsentForFutureRecruitments: string;
  status: string;
  stopApplicationProcessingDate: string;
  title: string;
};

export type AnalysisRemarksTranslation = {
  buttons: {
    list: string;
    submitRemark: string;
  };
  dialog: {
    buttons: {
      cancel: string;
      submit: string;
    };
    field: string;
    instruction: string;
    placeholder: string;
    title: string;
  };
  list: {
    close: string;
    noResults: string;
    title: string;
  };
};

export type CandidateDetailsTranslation = {
  about: string;
  agreements: AgreementsTranslation;
  analysisRemarks: AnalysisRemarksTranslation;
  companySize: CompanySizeTranslation;
  copyId: string;
  copyIdSuccess: string;
  currentEmployer: string;
  currentPosition: string;
  cvSearchContext: {
    description: string;
    loadError: string;
    title: string;
  };
  cvSearchTags: {
    addButton: string;
    addPlaceholder: string;
    clear: string;
    close: string;
    loadError: string;
    loadingOptions: string;
    manualTag: string;
    noOptions: string;
    open: string;
    removeTag: string;
    retry: string;
    title: string;
  };
  deleteCandidate: {
    button: string;
    dialog: {
      content: string;
    };
  };
  downloadCv: string;
  employer: {
    noData: string;
    products: string;
    services: string;
  };
  eRecruiterId: string;
  feedback: string;
  interviewSimulation: {
    buttons: {
      history: string;
    };
  };
  knowMeId: string;
  lastEmployer: string;
  match: string;
  notLinked: string;
  pick: {
    button: string;
    dialog: {
      content: string;
    };
    notExternalCandidate: string;
    selected: string;
  };
  quickTips: QuickTipsTranslation;
  quickView: {
    title: string;
  };
  rates: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    rating: string;
  };
  redFlags: string;
  refreshAnalysis: {
    button: string;
    dialog: {
      content: string;
    };
  };
  statuses: CandidateStatusesTranslation;
  strengths: string;
  tabs: Record<CandidateTabsModel, string>;
  tour: {
    analysisRemarksList: {
      text: string;
      title: string;
    };
    analysisRemarksSubmit: {
      text: string;
      title: string;
    };
    matchComponents: {
      text: string;
      title: string;
    };
    tags: {
      text: string;
      title: string;
    };
  };
  weaknesses: string;
};

export const candidateDetailsTranslation: CandidateDetailsTranslation = {
  about: 'About',
  agreements: {
    applicationDate: 'Application date',
    consentExpiresWithinMonth: 'The candidate’s data-processing consent expires within a month.',
    consentForFutureRecruitmentsGiven: 'Consent for future recruitments',
    consentForFutureRecruitmentsGranted: 'The candidate has consented to future recruitments.',
    cvFromCvSearch: 'CV from CV Search',
    dataProcessingConsentValidOverMonth: 'Data-processing consent is valid for more than a month.',
    hasProcessingTimeWarning: 'Processing time warning',
    limitedDataProcessing: 'Limited data processing',
    noConsentForFutureRecruitments: 'No consent for future recruitments.',
    status: 'Status',
    stopApplicationProcessingDate: 'Application processing stopped on',
    title: 'Consents',
  },
  analysisRemarks: {
    buttons: {
      list: 'View analysis remarks',
      submitRemark: 'Submit remark',
    },
    dialog: {
      buttons: {
        cancel: 'Cancel',
        submit: 'Submit Remarks',
      },
      field: 'Your remarks',
      instruction:
        'Please describe specifically what you disagree with in the analysis. Instead of general comments like "the rating should be higher", explain which skills, experience, or qualifications were incorrectly evaluated and why.',
      placeholder: 'Describe what is incorrect in the analysis and why...',
      title: 'Analysis Remarks',
    },
    list: {
      close: 'Close',
      noResults: 'No remarks have been submitted for this candidate yet.',
      title: 'Analysis Remarks',
    },
  },
  companySize: companySizeTranslation,
  copyId: 'Copy eRecruiter ID',
  copyIdSuccess: 'eRecruiter ID Copied to clipboard',
  currentEmployer: 'Current employer',
  currentPosition: 'Current Position',
  cvSearchContext: {
    description: 'Opened from CV Search "{{name}}".',
    loadError: 'Could not load CV Search details for this candidate view.',
    title: 'CV Search context',
  },
  cvSearchTags: {
    addButton: 'Add CV Search keyword',
    addPlaceholder: 'Search dictionary values',
    clear: 'Clear',
    close: 'Close',
    loadError: 'Failed to load CV Search keywords',
    loadingOptions: 'Loading…',
    manualTag: 'Manual keyword',
    noOptions: 'No options',
    open: 'Open',
    removeTag: 'Remove keyword',
    retry: 'Try again',
    title: 'CV Search keywords',
  },
  deleteCandidate: {
    button: 'Delete candidate',
    dialog: {
      content:
        'Are you sure you want to delete this candidate? All candidate data will be permanently deleted. This action cannot be undone.',
    },
  },
  downloadCv: 'Download CV',
  employer: {
    noData: 'No data',
    products: 'Products',
    services: 'Services',
  },
  eRecruiterId: 'eRecruiter ID',
  feedback: 'Feedback',
  interviewSimulation: {
    buttons: {
      history: 'Interview Simulation',
    },
  },
  knowMeId: 'KnowMe Id',
  lastEmployer: 'Last employer',
  match: 'Match score',
  notLinked: 'not linked with eRecruiter',
  pick: {
    button: 'Send to eRecruiter',
    dialog: {
      content: 'Are you sure you want to pick this candidate? Please confirm to proceed.',
    },
    notExternalCandidate: 'From PDF',
    selected: 'Already sent to eRecruiter',
  },
  quickTips: quickTipsTranslation,
  quickView: {
    title: 'Discovery Quick View',
  },
  rates: {
    1: '1 star: Misalignment',
    2: '2 stars: Weak Alignment',
    3: '3 stars: Satisfactory Alignment',
    4: '4 stars: Good Alignment',
    5: '5 stars: Ideal Alignment',
    rating: 'Rating',
  },
  redFlags: 'Red Flags',
  refreshAnalysis: {
    button: 'Refresh Analysis',
    dialog: {
      content: 'Are you sure you want to reprocess this candidate? Please confirm to proceed.',
    },
  },
  statuses: candidateStatusesTranslation,
  strengths: 'Strengths',
  tabs: {
    [CandidateTabsModel.CANDIDATE_SUMMARY]: 'Candidate summary',
    [CandidateTabsModel.MATCHING_RESULT]: 'Matching results',
  },
  tour: {
    analysisRemarksList: {
      text: 'Use this button to review comments that were already added to this candidate analysis.',
      title: 'View analysis comments',
    },
    analysisRemarksSubmit: {
      text: 'Use this button to add a comment to the candidate analysis. These comments help improve the application and future analyses.',
      title: 'Add analysis comment',
    },
    matchComponents: {
      text: 'For B2C processes, this section shows the candidate score components, including the main analysis and extra score factors.',
      title: 'Score components',
    },
    tags: {
      text: 'Keywords assigned to this candidate are displayed here.',
      title: 'Candidate keywords',
    },
  },
  weaknesses: 'Weaknesses',
};
